import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CourseDetail,
  Dashboard,
  DatasetSummary,
  DatasetTablesResponse,
  ExecuteQueryRequest,
  ExecuteQueryResponse,
  HintResponse,
  InterviewAnswerRequest,
  InterviewAnswerResponse,
  InterviewHistoryResponse,
  InterviewSession,
  LessonDetail,
  StartInterviewRequest,
  QueryHistoryResponse,
  QueryStatus,
  SubmissionResponse,
  TableDetail,
} from '@pglearn/shared';
import { api } from './api.js';

export function useDashboard() {
  return useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api.get<Dashboard>('/dashboard'),
    staleTime: 15_000,
  });
}

/**
 * Submits an answer for grading.
 *
 * Invalidates the dashboard, course and lesson caches on success, so awarded XP
 * and a newly solved exercise show up without a manual refresh.
 */
export function useSubmitExercise(exerciseId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sql: string) =>
      api.post<SubmissionResponse>(`/exercises/${exerciseId}/submit`, { sql }),
    onSuccess: (response) => {
      if (response.correct) {
        void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
        void queryClient.invalidateQueries({ queryKey: ['course'] });
        void queryClient.invalidateQueries({ queryKey: ['lesson'] });
      }
      void queryClient.invalidateQueries({ queryKey: ['query-history'] });
    },
  });
}

/** Runs an exercise query without grading it. */
export function useRunExercise(exerciseId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sql: string) =>
      api.post<ExecuteQueryResponse>(`/exercises/${exerciseId}/execute`, { sql }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['query-history'] });
    },
  });
}

export function useRevealHint(exerciseId: string) {
  return useMutation({
    mutationFn: () => api.post<HintResponse>(`/exercises/${exerciseId}/hint`),
  });
}

export function useCourse() {
  return useQuery({
    queryKey: ['course'],
    queryFn: () => api.get<CourseDetail>('/courses'),
    staleTime: 60_000,
  });
}

export function useLesson(slug: string | undefined) {
  return useQuery({
    queryKey: ['lesson', slug],
    queryFn: () => api.get<LessonDetail>(`/lessons/${slug!}`),
    enabled: Boolean(slug),
    staleTime: 60_000,
  });
}

export function useDatasets() {
  return useQuery({
    queryKey: ['datasets'],
    queryFn: () => api.get<{ datasets: DatasetSummary[] }>('/datasets'),
    // The dataset catalogue only changes when the seeder runs.
    staleTime: 30 * 60_000,
  });
}

export function useDatasetTables(slug: string | null) {
  return useQuery({
    queryKey: ['datasets', slug, 'tables'],
    queryFn: () => api.get<DatasetTablesResponse>(`/datasets/${slug!}/tables`),
    enabled: slug !== null,
    staleTime: 30 * 60_000,
  });
}

export function useTableDetail(slug: string | null, table: string | null) {
  return useQuery({
    queryKey: ['datasets', slug, 'tables', table],
    queryFn: () => api.get<TableDetail>(`/datasets/${slug!}/tables/${table!}`),
    enabled: slug !== null && table !== null,
    staleTime: 30 * 60_000,
  });
}

export interface QueryHistoryFilters {
  limit?: number;
  offset?: number;
  status?: QueryStatus;
  datasetSlug?: string;
  search?: string;
}

export function useQueryHistory(filters: QueryHistoryFilters | number = 25) {
  const resolved: QueryHistoryFilters =
    typeof filters === 'number' ? { limit: filters } : { limit: 25, ...filters };

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(resolved)) {
    if (value !== undefined && value !== '') params.set(key, String(value));
  }

  return useQuery({
    queryKey: ['query-history', params.toString()],
    queryFn: () => api.get<QueryHistoryResponse>(`/query/history?${params.toString()}`),
    staleTime: 0,
    // Keeps the previous page on screen while the next one loads, so paging and
    // typing in the search box do not blank the list on every keystroke.
    placeholderData: (previous) => previous,
  });
}

/**
 * Runs a query.
 *
 * A refused or failed query still resolves — the API returns a 200 with
 * `status: 'error'` and a friendly explanation, because a SQL error is a normal
 * part of learning rather than a request failure. Only transport and
 * authorisation problems reject.
 */
export function useExecuteQuery() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: ExecuteQueryRequest) =>
      api.post<ExecuteQueryResponse>('/query/execute', input),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['query-history'] });
    },
  });
}

// --- Interview mode ---------------------------------------------------------

export function useInterviewHistory() {
  return useQuery({
    queryKey: ['interview', 'history'],
    queryFn: () => api.get<InterviewHistoryResponse>('/interview/sessions'),
    staleTime: 0,
  });
}

export function useInterviewSession(id: string | undefined) {
  return useQuery({
    queryKey: ['interview', 'session', id],
    queryFn: () => api.get<InterviewSession>(`/interview/sessions/${id!}`),
    enabled: Boolean(id),
    // The question set is fixed server-side, so there is nothing to refetch;
    // answers update the cache directly from the mutation response.
    staleTime: Infinity,
  });
}

export function useStartInterview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: StartInterviewRequest) =>
      api.post<InterviewSession>('/interview/sessions', input),
    onSuccess: (session) => {
      queryClient.setQueryData(['interview', 'session', session.id], session);
      void queryClient.invalidateQueries({ queryKey: ['interview', 'history'] });
    },
  });
}

/**
 * Submits one answer. The response carries the refreshed session, so the
 * progress strip updates from the same round trip that graded the answer.
 */
export function useAnswerInterview(sessionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: InterviewAnswerRequest) =>
      api.post<InterviewAnswerResponse>(`/interview/sessions/${sessionId}/answer`, input),
    onSuccess: (response) => {
      queryClient.setQueryData(['interview', 'session', sessionId], response.session);
      if (response.submission.correct) {
        void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      }
      void queryClient.invalidateQueries({ queryKey: ['query-history'] });
    },
  });
}

export function useFinishInterview(sessionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.post<InterviewSession>(`/interview/sessions/${sessionId}/finish`),
    onSuccess: (session) => {
      queryClient.setQueryData(['interview', 'session', sessionId], session);
      void queryClient.invalidateQueries({ queryKey: ['interview', 'history'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}
